import React, { useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import useFormName from '../../../../../../Hooks/useFormName';
import ErrorMessage from '../ErrorMessage';
import TopFieldContent from '../TopFieldContent';
import CharactersCount from '../CharactersCount/CharactersCount';
import AddressWhisperer from '../../../AddressWhisperer/AddressWhisperer';
import type { AddressConfig } from '../../../AddressWhisperer/AddressWhisperer';
import InputWrapper from '../InputWrapper/InputWrapper';

/** Raw address lookup config from API (camelCase or snake_case) */
type AddressLookupSettings = Record<string, unknown>;

interface AddressWhispererFieldProps {
	name: string;
	label?: string;
	isRequired?: boolean;
	className?: string;
	placeholder?: string;
	charactersLimit?: number;
	showCharCounter?: boolean;
	isWrapped?: boolean;
	addressLookup: AddressLookupSettings;
}

function normalizeAddressConfig(
	raw: AddressLookupSettings,
): AddressConfig & { responseMapping?: Record<string, string> } {
	const endpoint =
		(raw.apiEndpoint as string) ??
		(raw.apiUrl as string) ??
		(raw.api_endpoint as string) ??
		(raw.api_url as string);
	const queryParam =
		(raw.queryParamName as string) ?? (raw.query_param_name as string) ?? 'q';
	const mapping =
		(raw.responseMapping as Record<string, string>) ??
		(raw.response_mapping as Record<string, string>) ??
		(raw.fieldMapping as Record<string, string>) ??
		(raw.field_mapping as Record<string, string>) ??
		{};
	return {
		apiEndpoint: endpoint,
		queryParamName: queryParam,
		responseMapping: mapping,
		fieldMapping: mapping,
	};
}

const AddressWhispererField: React.FC<AddressWhispererFieldProps> = ({
	name,
	label,
	isRequired,
	className,
	placeholder,
	charactersLimit,
	showCharCounter = true,
	addressLookup,
	isWrapped,
}) => {
	const { fieldName } = useFormName({ name });
	const { control, setValue } = useFormContext();
	const addressConfig = useMemo(
		() => normalizeAddressConfig(addressLookup),
		[addressLookup],
	);

	const {
		field,
		fieldState: { error },
	} = useController({
		control,
		name: fieldName,
		defaultValue: '',
	});

	const onAddressSelect = useCallback(
		(item: Record<string, unknown>) => {
			const mapping =
				addressConfig.responseMapping ?? addressConfig.fieldMapping ?? {};
			const prefix = fieldName.includes('.')
				? fieldName.slice(0, fieldName.lastIndexOf('.') + 1)
				: '';
			Object.entries(mapping).forEach(([formField, apiKey]) => {
				if (item[apiKey] != null) {
					const fullFieldName = prefix ? `${prefix}${formField}` : formField;
					setValue(fullFieldName, String(item[apiKey]), {
						shouldValidate: true,
					});
				}
			});
		},
		[addressConfig, fieldName, setValue],
	);

	const component = (
		<div className={className}>
			{label && <TopFieldContent label={label} isRequired={isRequired} />}
			<AddressWhisperer
				addressConfig={{ ...addressConfig, onAddressSelect }}
				value={field.value}
				onChange={field.onChange}
				placeholder={placeholder}
				label={undefined}
				name={field.name}
				ref={field.ref}
				className={error ? 'is-invalid' : undefined}
			/>
			{error?.message && <ErrorMessage tx={error.message} />}
			{charactersLimit && showCharCounter && (
				<CharactersCount limit={charactersLimit} value={field.value} />
			)}
		</div>
	);

	if (isWrapped) {
		return <InputWrapper>{component}</InputWrapper>;
	}

	return component;
};

export default AddressWhispererField;
